import { translate } from "@/plugins/i18n";
import type BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";
import { Maybe } from "@/components/utils/typeHelpers";


export class EnumIntegrationType implements BaseEnumType {
  private static allValues: EnumIntegrationType[] = []; // make sure this is top-most
  
  public static unknown = new EnumIntegrationType(0, "Unknown", "enum_integration_type_unknown");
  public static sellar = new EnumIntegrationType(1, "Sellar", "enum_integration_type_sellar");
  public static shopify = new EnumIntegrationType(2, "Shopify", "enum_integration_type_shopify");
  public static wooCommerce = new EnumIntegrationType(3, "WooCommerce", "enum_integration_type_woo_commerce");
  public static zettle = new EnumIntegrationType(4, "Zettle", "enum_integration_type_zettle");
  public static eposNow = new EnumIntegrationType(5, "EposNow", "enum_integration_type_epos_now");
  public static beerflex = new EnumIntegrationType(6, "Beerflex", "enum_integration_type_beerflex");
  public static square = new EnumIntegrationType(7, "Square", "enum_integration_type_square");
  public static squarespace = new EnumIntegrationType(8, "Squarespace", "enum_integration_type_squarespace");
  public static integrateOnce = new EnumIntegrationType(9, "IntegrateOnce", "enum_integration_type_integrate_once");
  public static ecwid = new EnumIntegrationType(10, "Ecwid", "enum_integration_type_ecwid");
  public static eeBria = new EnumIntegrationType(11, "EeBria", "enum_integration_type_ee_bria");
  public static commerce7 = new EnumIntegrationType(12, "Commerce7", "enum_integration_type_commerce7");
  public static amazon = new EnumIntegrationType(13, "Amazon", "enum_integration_type_amazon");
  public static bigCommerce = new EnumIntegrationType(14, "BigCommerce", "enum_integration_type_big_commerce");
  public static goTab = new EnumIntegrationType(15, "GoTab", "enum_integration_type_go_tab");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumIntegrationType.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isUnknown() { return this === EnumIntegrationType.unknown; }
  public isSellar() { return this === EnumIntegrationType.sellar; }
  public isShopify() { return this === EnumIntegrationType.shopify; }
  public isWooCommerce() { return this === EnumIntegrationType.wooCommerce; }
  public isZettle() { return this === EnumIntegrationType.zettle; }
  public isEposNow() { return this === EnumIntegrationType.eposNow; }
  public isBeerflex() { return this === EnumIntegrationType.beerflex; }
  public isSquare() { return this === EnumIntegrationType.square; }
  public isSquarespace() { return this === EnumIntegrationType.squarespace; }
  public isIntegrateOnce() { return this === EnumIntegrationType.integrateOnce; }
  public isEcwid() { return this === EnumIntegrationType.ecwid; }
  public isEeBria() { return this === EnumIntegrationType.eeBria; }
  public isCommerce7() { return this === EnumIntegrationType.commerce7; }
  public isAmazon() { return this === EnumIntegrationType.amazon; }
  public isBigCommerce() { return this === EnumIntegrationType.bigCommerce; }
  public isGoTab() { return this === EnumIntegrationType.goTab; }

  public static getValues(): readonly EnumIntegrationType[] {
    return EnumIntegrationType.allValues;
  }

  public static getValuesExcept0(): readonly EnumIntegrationType[] {
    return EnumIntegrationType.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumIntegrationType {
    const found = EnumIntegrationType.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumIntegrationType - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumIntegrationType): EnumIntegrationType {
    const found = name ? EnumIntegrationType.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumIntegrationType - Value not found: ${ name }`);
    }

    return found;
  }

  public equals(other: Maybe<EnumIntegrationType>): boolean {
    return this.key === other?.key;
  }
}